import { useGetStationDisplayName } from "@/entities/station";
import { Maatregel } from "@/entities/vsm";
import { useMaatregelVergelijking } from "@/features/vsm/vsm-vergelijking-context";
import { Icon } from "@/shared/components";
import { capitalizeFirstChar, cn } from "@/shared/utils";
import { gewijzigdMarkering } from "./gewijzigd-markering";

export function StationCode({ code, isGewijzigd = false }: Readonly<{ code: string; isGewijzigd?: boolean }>) {
    const getStationDisplayName = useGetStationDisplayName("withoutStationCode");

    return (
        <span
            className={cn("underline decoration-dotted", gewijzigdMarkering(isGewijzigd))}
            title={getStationDisplayName(code)}
            data-gewijzigd={isGewijzigd}
        >
            {capitalizeFirstChar(code)}
        </span>
    );
}

export function StationCodes({ codes, isGewijzigd = false }: Readonly<{ codes: string[]; isGewijzigd?: boolean }>) {
    return codes.map((code, index) => (
        <span key={code}>
            {index > 0 && ", "}
            <StationCode code={code} isGewijzigd={isGewijzigd} />
        </span>
    ));
}

export function TrajectIndicator({ maatregel }: Readonly<{ maatregel: Maatregel }>) {
    const { vanStationCode, naarStationCode, richting, vertrekTijd } = maatregel;
    const { gewijzigdeVelden } = useMaatregelVergelijking(maatregel);
    const isRichtingGewijzigd = gewijzigdeVelden.includes("richting");

    if (!vanStationCode || !naarStationCode) {
        return null;
    }

    return (
        <span className="text-s leading-l text-content-default group-data-[variant=oud]:text-content-subtle flex items-center gap-2">
            <VertrekTijd vertrekTijd={vertrekTijd} isGewijzigd={gewijzigdeVelden.includes("vertrekTijd")} />
            <StationCode code={vanStationCode} isGewijzigd={gewijzigdeVelden.includes("vanStationCode")} />
            <Icon
                size="xs"
                data-testid="maatregel-richting"
                data-richting={richting}
                data-gewijzigd={isRichtingGewijzigd}
                className={cn(
                    "text-content-emphasis group-data-[variant=oud]:text-content-subtle",
                    gewijzigdMarkering(isRichtingGewijzigd)
                )}
                name={richting === "ViceVersa" ? "arrow-right-left" : "arrow-right"}
            />
            <StationCode code={naarStationCode} isGewijzigd={gewijzigdeVelden.includes("naarStationCode")} />
        </span>
    );
}

export function VertrekTijd({ vertrekTijd, isGewijzigd = false }: Readonly<{ vertrekTijd?: string; isGewijzigd?: boolean }>) {
    return vertrekTijd ? (
        <span
            className={cn(
                "text-m leading-l group-data-[variant=nieuw]:text-content-emphasis group-data-[variant=view]:text-content-emphasis font-medium group-data-[variant=oud]:font-normal",
                gewijzigdMarkering(isGewijzigd)
            )}
            data-gewijzigd={isGewijzigd}
        >
            {vertrekTijd}{" "}
        </span>
    ) : null;
}
